/**
 * JUB-51: loads an event's short links once and exposes create/delete for the
 * Guests tab (GuestShortLinks renders them per guest row).
 *
 * Every request captures the event UUID and a generation number when it
 * starts. The generation moves forward when the event changes and on unmount,
 * so a late answer that belongs to another event (or to an unmounted tab) is
 * dropped instead of writing into the current list or raising a toast. List
 * loads also carry their own sequence number, so an older load cannot
 * overwrite a newer one (e.g. after Retry).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import {
  createShortLink,
  deleteShortLink,
  getEventShortLinks,
  type ShortLink,
} from "@/services/api/shortLinks";

export type EventShortLinksState = {
  links: ShortLink[];
  loading: boolean;
  loadFailed: boolean;
  retry: () => void;
  create: (guestUuid: string) => Promise<ShortLink | null>;
  remove: (link: ShortLink) => Promise<boolean>;
};

export function useEventShortLinks(eventUuid: string): EventShortLinksState {
  const { t, currentLanguage } = useTranslation();
  const [links, setLinks] = useState<ShortLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const eventRef = useRef(eventUuid);
  const generationRef = useRef(0);
  const loadSeqRef = useRef(0);

  const isCurrent = useCallback(
    (ev: string, generation: number) => eventRef.current === ev && generationRef.current === generation,
    [],
  );

  const load = useCallback((ev: string) => {
    const generation = generationRef.current;
    const seq = ++loadSeqRef.current;
    const stillWanted = () =>
      eventRef.current === ev && generationRef.current === generation && loadSeqRef.current === seq;
    setLoading(true);
    setLoadFailed(false);
    getEventShortLinks(ev)
      .then((data) => {
        if (!stillWanted()) return;
        setLinks(data);
        setLoading(false);
      })
      .catch(() => {
        if (!stillWanted()) return;
        setLinks([]);
        setLoadFailed(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    eventRef.current = eventUuid;
    generationRef.current += 1;
    setLinks([]);
    load(eventUuid);
    return () => {
      // Unmount or event switch: anything still in flight is stale.
      generationRef.current += 1;
    };
  }, [eventUuid, load]);

  const retry = useCallback(() => load(eventRef.current), [load]);

  const create = useCallback(
    async (guestUuid: string) => {
      const ev = eventRef.current;
      const generation = generationRef.current;
      try {
        const link = await createShortLink(ev, currentLanguage?.startsWith("en") ? "en" : "ro", guestUuid);
        if (!isCurrent(ev, generation)) return null;
        setLinks((prev) => [link, ...prev]);
        toast.success(t("shortLinks.created"));
        return link;
      } catch {
        if (isCurrent(ev, generation)) toast.error(t("shortLinks.createFailed"));
        return null;
      }
    },
    [currentLanguage, t, isCurrent],
  );

  const remove = useCallback(
    async (link: ShortLink) => {
      const ev = eventRef.current;
      const generation = generationRef.current;
      try {
        await deleteShortLink(ev, link.uuid);
        if (!isCurrent(ev, generation)) return false;
        setLinks((prev) => prev.filter((l) => l.uuid !== link.uuid));
        toast.success(t("shortLinks.deleted"));
        return true;
      } catch {
        if (isCurrent(ev, generation)) toast.error(t("shortLinks.deleteFailed"));
        return false;
      }
    },
    [t, isCurrent],
  );

  return { links, loading, loadFailed, retry, create, remove };
}

/** The full trackable URL, falling back to this origin when the API omits it. */
export function shortLinkUrl(link: ShortLink): string {
  if (link.shortUrl) return link.shortUrl;
  return `${window.location.origin}/e/${link.code}`;
}
